import { isApiError } from '@saay/api-client';
import { formatCount, type StatusEnum } from '@saay/formatting';
import { Button } from '@saay/shadcn/components/button';
import {
  CommandError,
  CommandFeedback,
  type CommandFeedbackState,
  Countdown,
  Icon,
  StatusChip,
  useSnackbar,
} from '../kit.js';
import { type ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

/** Status values that need no chip (XAP-16): the usual steady state only. */
const SILENT_STATUS: Partial<Record<StatusEnum, readonly string[]>> = {
  approvalRequestStatus: ['APPROVED'],
  buyerStatus: ['ACTIVE'],
  confirmationRequestStatus: ['CONFIRMED'],
  installmentStatus: ['UPCOMING'],
  merchantStatus: ['ACTIVE'],
  saleStatus: ['CONFIRMED'],
};

export function AbnormalStatusChip({
  enumName,
  value,
}: {
  readonly enumName: StatusEnum;
  readonly value: string;
}) {
  if (SILENT_STATUS[enumName]?.includes(value) === true) {
    return null;
  }
  return <StatusChip enumName={enumName} value={value} />;
}

export function useAdminFeedback() {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  return {
    approvalSubmitted(approvalRequestId: string) {
      snackbar.show({
        action: {
          kind: 'open',
          label: 'مشاهده',
          onAction: () => navigate(`/approvals/${approvalRequestId}`),
        },
        title: 'برای تأیید ارسال شد',
      });
    },
    copied() {
      snackbar.show({ title: 'کپی شد' });
    },
    success(title: string) {
      snackbar.show({ title });
    },
  };
}

/** Command feedback for dialogs: snackbar on approval, inline errors without technical request IDs. */
export function AdminCommandFeedback({
  onCheckStatus,
  pendingLabel,
  state,
}: {
  readonly onCheckStatus: () => void;
  readonly pendingLabel?: string;
  readonly state: CommandFeedbackState;
}) {
  if (state.status === 'approval-pending') {
    return null;
  }
  if (state.status === 'error') {
    // A business refusal (4xx) does not change by sending again; only a technical failure offers it
    // (W4: «فروشگاه تسویهٔ باقی‌مانده دارد» came with «تلاش دوباره»).
    const technical = !isApiError(state.error) || state.error.status >= 500;
    return (
      <CommandError
        {...(technical
          ? {
              action: (
                <Button onClick={onCheckStatus} type="button">
                  تلاش دوباره
                </Button>
              ),
            }
          : {})}
        error={state.error}
      />
    );
  }
  return (
    <CommandFeedback
      onCheckStatus={onCheckStatus}
      {...(pendingLabel === undefined ? {} : { pendingLabel })}
      state={state}
    />
  );
}

function remainingSeconds(expiresAt: string, now: number): number {
  const expiry = Date.parse(expiresAt);
  return Number.isNaN(expiry) ? 0 : Math.max(0, Math.ceil((expiry - now) / 1000));
}

/** «۲ روز و ۳ ساعت» or «۵ ساعت و ۱۰ دقیقه» for more than an hour left. */
export function remainingWords(seconds: number): string {
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const parts =
    days > 0
      ? [`${formatCount(days)} روز`, ...(hours > 0 ? [`${formatCount(hours)} ساعت`] : [])]
      : [`${formatCount(hours)} ساعت`, ...(minutes > 0 ? [`${formatCount(minutes)} دقیقه`] : [])];
  return parts.join(' و ');
}

/** Server-driven countdown with a clock icon and warning tone in the last minute (XAP-17). */
export function TimeRemaining({
  expiresAt,
  expiredLabel,
  label,
  onExpire,
}: {
  readonly expiresAt: string;
  readonly expiredLabel?: string;
  readonly label: string;
  readonly onExpire?: () => void;
}) {
  const [seconds, setSeconds] = useState(() => remainingSeconds(expiresAt, Date.now()));
  useEffect(() => {
    const timer = setInterval(() => {
      setSeconds(remainingSeconds(expiresAt, Date.now()));
    }, 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);
  const urgent = seconds > 0 && seconds <= 60;
  if (seconds > 60 * 60) {
    // Hours and days read as words; a minute:second clock only runs in the last hour.
    return (
      <span className="admin-time-remaining">
        <Icon name="clock" />
        <span className="saay-visually-hidden">{label}</span>
        <span>{remainingWords(seconds)}</span>
      </span>
    );
  }
  return (
    <span className="admin-time-remaining" data-urgent={urgent || undefined}>
      <Icon name="clock" />
      <Countdown
        {...(expiredLabel === undefined ? {} : { expiredLabel })}
        expiresAt={expiresAt}
        label={label}
        {...(onExpire === undefined ? {} : { onExpire })}
      />
    </span>
  );
}

export function PendingApprovalLabel(): ReactNode {
  return <StatusChip icon="clock" title="در انتظار تأیید" tone="warning" />;
}
